<template>
<NuxtLink
    class="internal-storefront"
    :class="['shopify', { disabled: isMaxQuantityReached }]"
    :aria-label="$t('PACK_DETAIL.ADD_TO_CART')"
    tabindex="0"
    role="button"
    v-on:click="onBuyClick()"
    v-on:keydown.enter="onBuyClick()"
>
    <div class="buy-for-text">
        {{ $t('PACK_DETAIL.ADD_TO_CART') }}
    </div>
    <span v-if="pack" class="icons">
        <NuxtImg class="windows" src="/images/icons/windows.svg" alt="windows" focusable="false" />
        <!-- TODO: temporarily hide non-windows plat icons for TMP3 early access. Bring them back once the builds are ready -->
        <NuxtImg
            v-if="pack.slug !== 'ydkj-classic' && pack.slug !== 'trivia-murder-party-3' && pack.slug !== 'the-jackbox-backpack-bundle'"
            src="/images/icons/apple.svg"
            class="apple"
            alt="apple"
            focusable="false"
        />
        <NuxtImg
            v-if="pack.slug !== 'ydkj-classic' && pack.slug !== 'trivia-murder-party-3' && pack.slug !== 'the-jackbox-backpack-bundle'"
            src="/images/icons/linux.svg"
            class="linux"
            alt="linux"
            focusable="false"
        />
    </span>
    <span v-else-if="cartIcon" class="icons">
        <i class="fa-duotone fa-solid fa-cart-plus" />
    </span>
</NuxtLink>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import type { Shopify } from '@jackboxgames/shopify'
import { ShoppingCart } from '$services/shopify'
import type { JBGWeb } from '$types/JBGWeb'

export default defineComponent({
    props: {
        product: Object as PropType<Shopify.Product | null>,
        variant: Object as PropType<Shopify.Variant | null>,
        pack: Object as PropType<JBGWeb.PackDetail | null>,
        cartIcon: Boolean
    },
    computed: {
        isMaxQuantityReached() {
            const variant = this.variant ?? this.product?.variants?.[0]
            if (!variant) {
                return false
            }

            const isSteam = ShoppingCart.isSteamVariant(variant.id)
            return isSteam && ShoppingCart.isVariantAtMaxQuantity(variant.id)
        }
    },
    methods: {
        async onBuyClick() {
            if (this.isMaxQuantityReached) {
                return
            }
            let variant = this.variant
            if (!variant) {
                variant = this.product.variants[0]
            }

            try {
                await this.$shopify.createLineItemAndAddToCart(this.product, variant, 1)
                ShoppingCart.open()
                // TODO: analytics
            } catch (error) {
                console.error('Failed to add item to cart:', error.message)
            }
        }
    }
})

</script>

<style lang="scss" scoped>
@use "$styles/kit.scss" as *;

.internal-storefront {
    background: linear-gradient(90deg, #9CA7FE 0%, #C0C7FF 100%);
    border: none;
    border-radius: 4px;
    color: var(--neutral-900);
    cursor: pointer;
    line-height: 18px;
    margin: 8px 0px 12px 0px;
    padding: 16px;

    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    @include mq-xsmall {
        padding: 12px;
        margin: 8px 0px 8px 0px;
    }

    &:hover:not(:disabled) {
        color: var(--active-tab);
        background: linear-gradient(90deg, #9CA7FE 0%, #C0C7FF 100%);
        box-shadow: 0px 0px 27.901px 0px #7b88ee, 0px 0px 1.329px 0px #7b88ee, 0px 0px 0.664px 0px #7b88ee;
    }

    &:active {
        background: linear-gradient(180deg, #8085a6 0%, #8F9CFF 100%);
    }

    &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
    }

    .buy-for-text {
        font-size: 16px;
        font-style: normal;
        font-weight: 800;
        line-height: 16px;
        margin-left: 4px;
        text-align: left;
        text-transform: uppercase;
        @include mq-xsmall {
            font-size: 14px;
            line-height: 14px;
            font-weight: 800;
        }
    }

    .icons {
        display: flex;
        gap: 8px;

        .windows {
            height: 16px;
            width: 17px;
        }
        .apple {
            height: 16px;
            width: 14px;
        }
        .linux {
            height: 18px;
            width: 15px;
        }
        .fa-cart-plus {
            width: 22px;
            height: 22px;
        }
    }
}
</style>
